import {updateProfilerRoute} from '@wandb/common/util/profiler';
import React, {ReactNode, useEffect} from 'react';
import {Route} from 'react-router';

import NoMatch from '../components/NoMatch';
import {isInIframe} from '../setup';

export type RouteWithLayoutProps = {
  component: React.ComponentType<any>;
  error?: any;
  allowIframes?: boolean;
  additionalProps?: {[prop: string]: any};
  [prop: string]: any;
};

// adds the Nav and Footer before and after the route component
const RouteWithLayout: React.FC<RouteWithLayoutProps> = ({
  component: Component,
  error,
  allowIframes,
  additionalProps = {},
  ...rest
}) => {
  const preventIframe = isInIframe() && !allowIframes;
  return (
    <Route
      {...rest}
      render={routeProps => {
        return preventIframe ? (
          <NoMatch unrecoverable />
        ) : (
          <RouteNameWrapper
            routeName={rest.routeName}
            tab={rest.computedMatch?.params?.tab ?? additionalProps.tab}>
            <Component error={error} {...routeProps} {...additionalProps} />
          </RouteNameWrapper>
        );
      }}
    />
  );
};

// This component takes the pageName property defined on the
// route and passes it along to the profiler so we know what
// page we're on.
//
// Design note: This seems like a complicated way to do this,
// but it's the simplest one I could find :) RouteWithLayout is responsible
// for defining the <Route>'s render() which is
// the only place where we have access to the Route and we know
// that the route is actually being used. useRouteMatch in theory
// provides this information as well, but it was not consistently
// returning the correct route when I tried it out. We would still
// need to define the pageName on the Routes in that solution as well,
// and we'd need a high level place to put the call, which would end
// up being... RouteWithLayout :)
const RouteNameWrapper = ({
  children,
  routeName,
  tab,
}: {
  children: ReactNode;
  routeName?: string;
  tab?: string;
}) => {
  useEffect(() => {
    const tabSuffix = tab ? `:${tab}` : '';
    updateProfilerRoute(routeName + tabSuffix);
    // Why does this need clean up? If the route we're going to
    // isn't a RouteWithLayout, it won't call updateProfilerRoute.
    // So, by defining a cleanup that clears the route name, we ensure
    // that the profiler is updated to not have a route name when the
    // user leaves the page.
    // There are few pages that don't use RouteWithLayout, so this is
    // mostly for future-proofing.
    return () => updateProfilerRoute();
  }, [routeName, tab]);
  return <>{children}</>;
};

export default RouteWithLayout;
